import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/apiConfig';
import { socket } from '../config/socket';
import './MesaPedidos.css';


const MesaPedidosComponent = () => {
  const { mesaId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [mesa, setMesa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mensajeAccion, setMensajeAccion] = useState('');

  // Función para cargar los datos de la mesa y sus pedidos de forma optimizada
  const cargarDatosMesa = useCallback(async (silencioso = false) => {
    try {
      if (!silencioso) setLoading(true);

      // Obtener información de la mesa
      const responseMesa = await fetch(getApiUrl(`/mesas/${mesaId}`), {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });

      if (!responseMesa.ok) {
        if (!silencioso) throw new Error('Error al cargar información de la mesa');
        return;
      }

      const dataMesa = await responseMesa.json();
      
      // Obtener pedidos activos específicamente para la mesa
      const responsePedidos = await fetch(getApiUrl(`/pedidos/mesa/${mesaId}?estado_mesa=ocupada`), {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });
      
      if (!responsePedidos.ok) {
        if (!silencioso) throw new Error('Error al cargar pedidos de la mesa');
        return;
      }
      
      const pedidosActivos = await responsePedidos.json();
      
      // Asignar los pedidos obtenidos directamente a la propiedad pedidos de la mesa
      dataMesa.pedidos = pedidosActivos;
      
      // Actualizar el estado de mesa de forma segura
      setMesa(prevMesa => {
        if (!silencioso || JSON.stringify(dataMesa) !== JSON.stringify(prevMesa)) {
          return dataMesa;
        }
        return prevMesa;
      });
      
      if (!silencioso) setLoading(false);
    } catch (err) {
      if (!silencioso) {
        setError(err.message);
        setLoading(false);
      }
    }
  }, [mesaId, auth?.token]);

  useEffect(() => {
    cargarDatosMesa(false);
    
    const handleSocketUpdate = (data) => {
      console.log('Evento de socket recibido en MesaPedidosComponent:', data);
      cargarDatosMesa(true);
    };

    socket.on('pedidoCreado', handleSocketUpdate);
    socket.on('cambioEstadoComanda', handleSocketUpdate);
    
    return () => {
      socket.off('pedidoCreado', handleSocketUpdate);
      socket.off('cambioEstadoComanda', handleSocketUpdate);
    };
  }, [cargarDatosMesa]);

  const actualizarEstadoMesa = async (idMesa, nuevoEstado) => {
    try {
      const response = await fetch(getApiUrl(`/mesas/${idMesa}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({ 
          estado: nuevoEstado,
          id_mesero: null // Si es necesario mantener el mesero actual, modificar esta línea
        })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar el estado de la mesa');
      }

      console.log(`Estado de la mesa actualizado a: ${nuevoEstado.replace('_', ' ')}`);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const liberarMesa = async () => {
    try {
      setMensajeAccion('Verificando pedidos pendientes...');
      
      // Verificar si todos los pedidos están atendidos (cancelados o pagados)
      const pedidosPendientes = mesa.pedidos.filter(
        pedido => pedido.estado !== 'cancelado' && pedido.estado !== 'pagado'
      );
      
      if (pedidosPendientes.length > 0) {
        setError("No se puede liberar la mesa. Hay pedidos pendientes de pago.");
        setMensajeAccion('');
        return;
      }
      
      setMensajeAccion('Actualizando estado de pedidos...');
      
      // Usar la ruta para actualizar todos los pedidos de la mesa de una vez
      const responsePedidos = await fetch(getApiUrl(`/pedidos/mesa/${mesaId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({ 
          estado_mesa: 'libre'
        })
      });
      
      if (!responsePedidos.ok) {
        const errorData = await responsePedidos.json();
        throw new Error(errorData.message || 'Error al actualizar los pedidos');
      }
      
      const responseData = await responsePedidos.json();
      console.log("Respuesta al liberar mesa:", responseData);
      
      // Actualizar el estado de la mesa a 'libre'
      setMensajeAccion('Liberando mesa...');
      const mesaActualizada = await actualizarEstadoMesa(mesaId, 'libre');
      
      if (!mesaActualizada) {
        throw new Error('Error al liberar la mesa');
      }
      
      // Actualizar el estado local explícitamente
      setMesa(prevMesa => ({
        ...prevMesa,
        pedidos: [], // Limpiar pedidos después de liberar la mesa
        estado: 'libre'
      }));
      
      setMensajeAccion('¡Mesa liberada correctamente!');
      
      setMensajeAccion('');
      navigate('/mesas');
      
    } catch (err) {
      setError(err.message);
      setMensajeAccion('');
    }
  };

  const formatearTiempo = (timestamp) => {
    const fecha = new Date(timestamp);
    return fecha.toLocaleString();
  };

  const calcularTiempoTranscurrido = (timestamp) => {
    const fecha = new Date(timestamp);
    const ahora = new Date();
    const diferencia = Math.floor((ahora - fecha) / 1000 / 60); // en minutos

    if (diferencia < 60) {
      return `${diferencia} min`;
    } else {
      const horas = Math.floor(diferencia / 60);
      const minutos = diferencia % 60;
      return `${horas}h ${minutos}m`;
    }
  };

  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'pendiente': return 'estado-pendiente';
      case 'en_prep': return 'estado-preparacion';
      case 'listo': return 'estado-listo';
      case 'entregado': return 'estado-entregado';
      case 'cancelado': return 'estado-cancelado';
      case 'pagado': return 'estado-pagado';
      case 'listo_pagado': return 'estado-listo-pagado';
      default: return '';
    }
  };

  // Nueva función para verificar si un pedido tiene productos listos
  const tieneProdutosListos = (pedido) => {
    // Revisar si el pedido mismo está en estado listo_pagado o listo
    if (pedido.estado === 'listo_pagado' || pedido.estado === 'listo') {
      return true;
    }
    
    // Revisar si alguno de los detalles está en estado listo, listo_pagado o similares
    if (pedido.detalles && pedido.detalles.length > 0) {
      return pedido.detalles.some(detalle => 
        detalle.estado === 'listo' || 
        detalle.estado === 'listo_pagado'
      );
    }
    
    return false;
  };

  const verDetallePedido = (pedidoId) => {
    console.log("🚀 ~ verDetallePedido ~ pedidoId:", pedidoId)
    navigate(`/pedidos/${pedidoId}`);
  };

  if (loading) {
    return <div className="loading">Cargando pedidos...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">Error: {error}</div>
        <button
          onClick={() => {
            setError(null);
            navigate('/mesas');
          }}
          className="btn-volver"
        >
          Volver a Mesas
        </button>
      </div>
    );
  }

  return (
    <div className="mesa-pedidos-container">
      <div className="mesa-pedidos-header">
        <h2>Pedidos de Mesa {mesa?.numero}</h2>
        <div className="header-actions">
          <button 
            onClick={liberarMesa} 
            className="btn-liberar"
            disabled={mesa?.estado === 'libre'}
          >
            Liberar Mesa
          </button>
          <button
            className="btn-nuevo"
            onClick={() => navigate(`/pedidos/nuevo/${mesaId}`)}
            disabled={mesa?.estado === 'libre'}
          >
            Nuevo Pedido
          </button>
          <button
            className="btn-volver"
            onClick={() => navigate('/mesas')}
          >
            Volver a Mesas
          </button>
        </div>
      </div>

      {mensajeAccion && (
        <div className="mensaje-accion">
          {mensajeAccion}
        </div>
      )}

      {(!mesa.pedidos || mesa.pedidos.length === 0) ? (
        <div className="sin-pedidos">
          <p>No hay pedidos activos para esta mesa</p>
        </div>
      ) : (
        <div className="lista-pedidos">
          {mesa.pedidos.map(pedido => (
            <div
              key={pedido.id}
              className={`pedido-card ${getEstadoClass(pedido.estado)} ${tieneProdutosListos(pedido) ? 'producto-listo parpadeo' : ''}`}
              onClick={() => verDetallePedido(pedido.id)}
            >
              <div className="pedido-header">
                <div className="pedido-header-top">
                  <div className="pedido-id">Pedido #{pedido.id}</div>
                  <div className="pedido-estado">{pedido.estado.replace('_', ' ')}</div>
                </div>
                {tieneProdutosListos(pedido) && (
                  <div className="indicador-productos-listos">¡Productos listos!</div>
                )}
              </div>
              <div className="pedido-tiempo">
                <span>Inicio: {formatearTiempo(pedido.tiempo_inicio)}</span>
                <span>Tiempo: {calcularTiempoTranscurrido(pedido.tiempo_inicio)}</span>
              </div>
              <div className="pedido-productos">
                {pedido.detalles ? (
                  (() => {
                    const detallesActivos = pedido.detalles.filter(d => d.estado !== 'cancelado');
                    const agrupados = {};
                    detallesActivos.forEach(d => {
                      const clave = `${d.id_producto}-${d.nombre_producto}`;
                      if (!agrupados[clave]) {
                        agrupados[clave] = { ...d, cantidad_total: d.cantidad, subtotal: d.cantidad * d.precio_unitario };
                      } else {
                        agrupados[clave].cantidad_total += d.cantidad;
                        agrupados[clave].subtotal += d.cantidad * d.precio_unitario;
                      }
                    });
                    if (Object.keys(agrupados).length === 0) {
                      return <div className="sin-productos">Sin productos activos</div>;
                    }
                    return Object.values(agrupados).map((p, i) => (
                      <div key={i} className="pedido-producto-fila">
                        <span className="pp-nombre">
                          {p.nombre_producto}
                          {p.estado === 'listo' && <span className="producto-estado-listo"> ✓</span>}
                        </span>
                        <span className="pp-cantidad">×{p.cantidad_total}</span>
                        <span className="pp-subtotal">S/. {p.subtotal.toFixed(2)}</span>
                      </div>
                    ));
                  })()
                ) : 'No disponible'}
              </div>
              <div className="pedido-total">
                <span>Total: S/. {pedido.total}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MesaPedidosComponent;